import { EPartKind, IProductionPart, productionParts } from '@stair-craft/calc/util';
import { IStairCalculationResult, IStairConfig, IStringerType, IWoodMaterial, WOOD_MATERIALS } from '@stair-craft/common/util';

import { writePartDxf } from './dxf-part-writer';
import { IZipEntry, zipStore } from './zip-store';

/** The folder the wooden parts lie in, as the production sample keeps them. */
const WOOD_FOLDER: string = $localize`:@@dxfPartsFolderWood:Дерево`;
const UNIT_MM: string = $localize`:@@dxfPartsUnitMm:мм`;
const UNIT_PIECES: string = $localize`:@@dxfPartsUnitPieces:шт`;

function kindName(kind: EPartKind, stringerType: IStringerType): string {
    switch (kind) {
        case EPartKind.Tread:
            return $localize`:@@dxfPartTread:Ступень`;
        case EPartKind.Landing:
            return $localize`:@@dxfPartLanding:Площадка`;
        case EPartKind.Riser:
            return $localize`:@@dxfPartRiser:Подступёнок`;
        case EPartKind.Stringer:
            return stringerType === 'kosour' ? $localize`:@@dxfPartKosour:Косоур` : $localize`:@@dxfPartTetiva:Тетива`;
        case EPartKind.SupportPost:
            return $localize`:@@dxfPartSupportPost:Опорный столб`;
    }
}

function materialName(id: string): string {
    return WOOD_MATERIALS.find((material: IWoodMaterial): boolean => material.id === id)?.name ?? id;
}

/** Characters a file system refuses in a name are replaced, so every system unpacks the archive. */
function safeName(name: string): string {
    return name.replace(/[\\/:*?"<>|]/g, '-');
}

/**
 * The file names of the parts, `<part>_<species>_<thickness>мм_<N>шт.dxf`: a kind with several
 * different parts numbers them `<part>-1`, `<part>-2` in list order.
 */
export function partFileNames(parts: readonly IProductionPart[], stringerType: IStringerType): string[] {
    const perKind: Map<EPartKind, number> = new Map<EPartKind, number>();
    parts.forEach((part: IProductionPart): void => {
        perKind.set(part.kind, (perKind.get(part.kind) ?? 0) + 1);
    });
    const seen: Map<EPartKind, number> = new Map<EPartKind, number>();
    return parts.map((part: IProductionPart): string => {
        const index: number = (seen.get(part.kind) ?? 0) + 1;
        seen.set(part.kind, index);
        const base: string = kindName(part.kind, stringerType);
        const name: string = (perKind.get(part.kind) ?? 0) > 1 ? `${base}-${String(index)}` : base;
        const thickness: string = String(Math.round(part.thickness));
        return safeName(`${name}_${materialName(part.materialId)}_${thickness}${UNIT_MM}_${String(part.count)}${UNIT_PIECES}.dxf`);
    });
}

/**
 * The parts archive of a calculation: a zip with one DXF per distinct board — treads, landings,
 * risers, stringers — in the folder «Дерево», alike boards counted in the name. `date` is the time
 * the entries carry.
 */
export function writePartsArchive(result: IStairCalculationResult, config: IStairConfig, date: Date): Uint8Array<ArrayBuffer> {
    const parts: IProductionPart[] = productionParts(result, config);
    const names: string[] = partFileNames(parts, config.stringerType);
    const encoder: TextEncoder = new TextEncoder();
    const entries: IZipEntry[] = parts.map((part: IProductionPart, index: number): IZipEntry => ({
        path: `${WOOD_FOLDER}/${names[index]}`,
        data: encoder.encode(writePartDxf(part.outline)),
    }));
    return zipStore(entries, date);
}
